import { Group, Select, rem } from '@mantine/core';
import { GridApi } from 'ag-grid-community';
import { PriceOfferRow } from 'components/tables/data/price-offer/types';
import useFilterValue, { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import P4Medium from 'components/typography/P4Medium';
import { forwardRef, useCallback, useEffect, useState } from 'react';

export const FILTER_KEY = 'device.deviceId' as const;

interface DeviceOption {
  value: string;
  label: string;
  deviceId: number;
  deviceName: string;
}

interface DeviceSelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  deviceId: number;
  deviceName: string;
}

interface PriceOfferDeviceSelectFilterProps {
  api: GridApi<PriceOfferRow>;
  onChange?: FilterChangeCallback;
}

const DeviceSelectItem = forwardRef<HTMLDivElement, DeviceSelectItemProps>(
  ({ deviceId, deviceName, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap spacing={4} align="baseline">
        <P4Medium c="gray.4">(#{deviceId})</P4Medium>
        {deviceName}
      </Group>
    </div>
  )
);

DeviceSelectItem.displayName = 'PriceOfferDeviceSelectFilterItem';

/**
 * Device filter for the price-offer table.
 */
export default function PriceOfferDeviceSelectFilter({ api, onChange }: PriceOfferDeviceSelectFilterProps) {
  const [value, setValue] = useFilterValue({ api, filterKey: FILTER_KEY, onChange });
  const [options, setOptions] = useState<DeviceOption[]>([]);

  const refreshOptions = useCallback(() => {
    const devices = new Map<number, DeviceOption>();

    api.forEachNode(({ data }) => {
      if (!data?.device) {
        return;
      }

      devices.set(data.device.deviceId, {
        value: String(data.device.deviceId),
        label: `(#${data.device.deviceId}) ${data.device.deviceName}`,
        deviceId: data.device.deviceId,
        deviceName: data.device.deviceName,
      });
    });

    setOptions(
      Array.from(devices.values()).sort((a, b) =>
        a.deviceName.localeCompare(b.deviceName, 'sk', { sensitivity: 'base' })
      )
    );
  }, [api]);

  useEffect(() => {
    refreshOptions();

    api.addEventListener('rowDataUpdated', refreshOptions);
    api.addEventListener('modelUpdated', refreshOptions);

    return () => {
      api.removeEventListener('rowDataUpdated', refreshOptions);
      api.removeEventListener('modelUpdated', refreshOptions);
    };
  }, [api, refreshOptions]);

  return (
    <Select
      clearable
      searchable
      w="100%"
      size="sm"
      placeholder="Všetky zariadenia"
      value={value}
      onChange={setValue}
      data={options}
      itemComponent={DeviceSelectItem}
      nothingFound="Žiadne výsledky"
      styles={{ dropdown: { minWidth: rem(400) } }}
    />
  );
}
